import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { usePublicForm, usePublicFormPage, useSubmitPublicForm } from '../../../hooks/usePublicForm';
import { useTeamContext } from '../../../contexts/TeamContext';
import { useFormDraft } from '../../../hooks/useFormDraft';
import { usePageValidation } from '../../../hooks/usePageValidation';
import { PageStyleProvider } from '../../../providers/PageStyleProvider';
import { StyleProvider } from '../../../providers/StyleProvider';
import { useStyle } from '../../../contexts/StyleContext';
import { PublicTopNavigation } from '../PublicTopNavigation';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { FormProgressBar } from './FormProgressBar';
import { FormNavigation } from './FormNavigation';
import { FormPage } from './FormPage';
import { FormConfirmation } from './FormConfirmation';
import { FormWelcome } from './FormWelcome';
import { FormBuiltinFields } from './FormBuiltinFields';
import { AutosaveIndicator } from './AutosaveIndicator';
import { FormContext } from './FormContext';
import { AuthenticationRequired } from '@portal/components/common/AuthenticationRequired';
import type { FormState, Question, FormResponses } from '../../../types/publicForm.types';

// Internal component that uses the style context
const PublicFormContent: React.FC = () => {
  const { formSlug } = useParams<{ formSlug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { teamSlug } = useTeamContext();
  const { style: contextStyle } = useStyle();
  const { currentUser } = useCurrentUser();

  // Fetch form data
  const { data: form, isLoading: isFormLoading, error: formError } = usePublicForm(formSlug || '');

  // Form state
  const [formState, setFormState] = useState<FormState>({
    currentPageIndex: 0,
    formData: {},
    touchedFields: new Set(),
    visibleChildQuestions: new Set(),
    validationErrors: {},
    isSubmitting: false,
    showConfirmation: false,
  });

  // Welcome page state - check if we should skip it based on URL or query params
  const urlParams = new URLSearchParams(location.search);
  const isReturningFromAuth = urlParams.get('authenticated') === 'true';
  const shouldSkipWelcome = location.pathname.endsWith('/new') || isReturningFromAuth;
  const [showWelcomePage, setShowWelcomePage] = useState(!shouldSkipWelcome);
  const [hasStartedForm, setHasStartedForm] = useState(shouldSkipWelcome); // Start immediately if URL ends with /new or returning from auth
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [requiresAuth, setRequiresAuth] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [builtinFieldsCompleted, setBuiltinFieldsCompleted] = useState(false);
  const [isStartingFresh, setIsStartingFresh] = useState(false);

  // Clear existing draft if accessing via /new URL but not when returning from auth
  useEffect(() => {
    if (location.pathname.endsWith('/new') && form?.slug) {
      const draftKey = `form_draft_${form.slug}`;
      localStorage.removeItem(draftKey);
      // Also clear from query cache to force refetch
      queryClient.removeQueries({ queryKey: ['formDraft', form.slug] });
      // Reset built-in fields for new submission
      setBuiltinFieldsCompleted(false);
    }
  }, [location.pathname, form?.slug]);

  // Check authentication requirements when form loads
  useEffect(() => {
    if (form) {
      setRequiresAuth(form.requiresAccount || false);
      setIsAuthenticated(!!currentUser);
    }
  }, [form, currentUser]);

  // Clean up the authenticated parameter from URL and clear the stored location cookie
  useEffect(() => {
    if (isReturningFromAuth) {
      // Remove the authenticated parameter from the URL without causing a page reload
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete('authenticated');
      const newUrl = currentUrl.pathname + currentUrl.search;
      window.history.replaceState({}, '', newUrl);

      // Clear the stored_account_location cookie to prevent infinite redirects
      // Get the base domain for cookie deletion
      const domain = window.location.hostname.split('.').slice(-2).join('.');
      // Clear the cookie by setting it with an expired date
      document.cookie = `stored_account_location=;domain=.${domain};path=/;expires=Thu, 01 Jan 1970 00:00:00 UTC`;
      // Also try clearing without domain in case it was set differently
      document.cookie = `stored_account_location=;path=/;expires=Thu, 01 Jan 1970 00:00:00 UTC`;
    }
  }, [isReturningFromAuth]);

  // Draft management - use form slug instead of ID for API calls
  // Only initialize draft after user starts the form
  const {
    draft,
    isDraftLoading,
    isSaving,
    lastSaved,
    saveError,
    saveChanges,
    saveNow,
    clearDraft
  } = useFormDraft(hasStartedForm ? (form?.slug || formSlug || '') : '');

  // Reset starting fresh flag once draft is cleared
  useEffect(() => {
    if (isStartingFresh && !draft && !isDraftLoading) {
      setIsStartingFresh(false);
    }
  }, [isStartingFresh, draft, isDraftLoading]);

  // Page validation
  const { validatePage, validatePageWithServer } = usePageValidation(form?.slug || formSlug || '', formState);

  // Fetch current page data
  const { data: pageData, isLoading: isPageLoading } = usePublicFormPage(
    form?.slug || formSlug || '',
    formState.currentPageIndex
  );

  // Check if we should show built-in fields
  const shouldShowBuiltinFields = useCallback(() => {
    console.log('Checking if built-in fields should be shown');
    if (!form) return false;

    console.log('Form allows anonymous:', form.allowsAnonymous, 'Options:', form.options);

    // Check if form allows anonymous submissions (from API response)
    if (form.allowsAnonymous || form.options?.allow_anonymous) return false;

    // Always show if not anonymous (at minimum for name/email)
    return true;
  }, [form]);

  // Load draft data on mount
  useEffect(() => {
    if (draft && !isDraftLoading && Object.keys(formState.formData).length === 0 && !isStartingFresh) {
      // Load submission data from draft
      const draftData: Record<string, any> = {};
      let hasBuiltinData = false;

      // Load basic fields
      if (draft.submission?.firstName) {
        draftData.firstName = draft.submission.firstName;
        hasBuiltinData = true;
      }
      if (draft.submission?.lastName) {
        draftData.lastName = draft.submission.lastName;
        hasBuiltinData = true;
      }
      if (draft.submission?.email) {
        draftData.email = draft.submission.email;
        hasBuiltinData = true;
      }
      if (draft.submission?.company) draftData.company = draft.submission.company;
      if (draft.submission?.phone) draftData.phone = draft.submission.phone;

      // Load address fields
      if (draft.submission?.addressAttributes) {
        const addr = draft.submission.addressAttributes;
        draftData.name = addr.name;
        draftData.address1 = addr.address1;
        draftData.address2 = addr.address2;
        draftData.city = addr.city;
        draftData.state = addr.state;
        draftData.zip = addr.zip;
        draftData.country = addr.country;
      }

      // Load question responses
      Object.entries(draft.responses).forEach(([questionId, response]) => {
        if (response.value !== undefined) {
          draftData[questionId] = response.value;
        } else if (response.answerId) {
          // For dropdown/select questions, store just the answer ID as a string
          draftData[questionId] = response.answerId;
        } else if (response.answerIds) {
          draftData[questionId] = response.answerIds;
        } else if (response.files && response.files.length > 0) {
          // Handle file uploads - take the first file for now
          const file = response.files[0];
          draftData[questionId] = {
            fileKey: file.key,
            fileName: file.filename
          };
        }

        if (response.comment) {
          draftData[`${questionId}_comment`] = response.comment;
        }
      });

      setFormState(prev => ({
        ...prev,
        formData: draftData,
        draft: draft,
      }));

      // Check if built-in fields were already completed
      // Only mark as completed if we have all required fields AND we're not starting fresh
      if (hasBuiltinData && shouldShowBuiltinFields() && !isStartingFresh) {
        const hasRequiredFields = draftData.firstName && draftData.lastName && draftData.email;
        const hasCompany = !form?.options?.requireCompany || form?.options?.requireCompany === '0' || draftData.company;
        const hasPhone = !form?.options?.requirePhone || form?.options?.requirePhone === '0' || draftData.phone;
        const hasAddress = !form?.options?.requireAddress || form?.options?.requireAddress === '0' ||
                          (draftData.address1 && draftData.city && draftData.state && draftData.zip);

        if (hasRequiredFields && hasCompany && hasPhone && hasAddress) {
          console.log('Setting builtinFieldsCompleted to true because draft has all required fields');
          setBuiltinFieldsCompleted(true);
        }
      }
    }
  }, [draft, isDraftLoading, shouldShowBuiltinFields, form, isStartingFresh]);

  // Form submission mutation
  const submitMutation = useSubmitPublicForm();

  // Use colors from the merged style context
  const primaryColor = contextStyle?.headerColor || '#3B82F6';

  // Helper to get a question by ID from current page data (including conditional questions)
  const getQuestionById = useCallback((questionId: string): Question | null => {
    if (!pageData?.page) return null;

    // Recursive function to search through questions and their conditional children
    const searchQuestion = (question: Question): Question | null => {
      if (question.id === questionId) {
        return question;
      }
      
      // Search through conditional questions (childQuestions in answers)
      if (question.answers) {
        for (const answer of question.answers) {
          if (answer.childQuestions) {
            for (const childQuestion of answer.childQuestions) {
              const found = searchQuestion(childQuestion);
              if (found) return found;
            }
          }
        }
      }
      
      return null;
    };

    // Search through all questions in all sections
    for (const section of pageData.page.surveySections) {
      for (const question of section.questions) {
        const found = searchQuestion(question);
        if (found) return found;
      }
    }
    return null;
  }, [pageData]);

  // Form actions
  const updateField = useCallback((questionId: string, value: any, batchedAddressUpdate?: Record<string, any>) => {
    setFormState(prev => {
      // Clear validation error for this field if it now has a valid value
      const newValidationErrors = { ...prev.validationErrors };
      // For required fields, check if value is not empty string, null, undefined, or empty array
      const hasValidValue = value !== '' && value !== null && value !== undefined &&
                           (Array.isArray(value) ? value.length > 0 : true);

      if (hasValidValue && newValidationErrors[questionId]) {
        delete newValidationErrors[questionId];
      }

      // If this is part of a batched address update, update all address fields at once
      if (batchedAddressUpdate) {
        return {
          ...prev,
          formData: {
            ...prev.formData,
            ...batchedAddressUpdate,
          },
          validationErrors: newValidationErrors,
        };
      }

      return {
        ...prev,
        formData: {
          ...prev.formData,
          [questionId]: value,
        },
        validationErrors: newValidationErrors,
      };
    });

    // Autosave changes
    if (draft && form) {
      const updates: any = { responses: {} };

      // Check if it's a user field
      const userFields = ['firstName', 'lastName', 'email', 'company', 'phone'];
      const addressFields = ['name', 'address1', 'address2', 'city', 'state', 'zip', 'country'];

      if (userFields.includes(questionId)) {
        if (!updates.submission) updates.submission = {};
        updates.submission[questionId] = value;
      } else if (addressFields.includes(questionId)) {
        if (!updates.submission) updates.submission = {};
        // If we have a batched address update, use those values directly
        if (batchedAddressUpdate) {
          updates.submission.addressAttributes = {
            name: batchedAddressUpdate.name || '',
            address_1: batchedAddressUpdate.address1 || '',
            address_2: batchedAddressUpdate.address2 || '',
            city: batchedAddressUpdate.city || '',
            state: batchedAddressUpdate.state || '',
            zip: batchedAddressUpdate.zip || '',
            country: batchedAddressUpdate.country || 'US'
          };
        } else {
          // Single address field update - still use all fields together
          updates.submission.addressAttributes = {
            name: questionId === 'name' ? value : (formState.formData.name || ''),
            address_1: questionId === 'address1' ? value : (formState.formData.address1 || ''),
            address_2: questionId === 'address2' ? value : (formState.formData.address2 || ''),
            city: questionId === 'city' ? value : (formState.formData.city || ''),
            state: questionId === 'state' ? value : (formState.formData.state || ''),
            zip: questionId === 'zip' ? value : (formState.formData.zip || ''),
            country: questionId === 'country' ? value : (formState.formData.country || 'US')
          };
        }
      } else {
        // It's a question response
        if (questionId.endsWith('_comment')) {
          const baseQuestionId = questionId.replace('_comment', '');
          if (!updates.responses[baseQuestionId]) {
            updates.responses[baseQuestionId] = {};
          }
          updates.responses[baseQuestionId].comment = value;
        } else if (Array.isArray(value)) {
          updates.responses[questionId] = { answerIds: value };
        } else if (typeof value === 'object' && value !== null && 'answerId' in value) {
          updates.responses[questionId] = { answerId: value.answerId };
        } else if (typeof value === 'object' && value !== null && 'fileKey' in value) {
          // Handle file upload - transform to array format for backend
          updates.responses[questionId] = {
            files: [{
              key: value.fileKey,
              filename: value.fileName || 'file'
            }]
          };
        } else if (typeof value === 'object' && value !== null && ('tagIds' in value || 'tagRequests' in value || 'questionResponseTaggings' in value)) {
          // Tag question - save the complex data structure
          updates.responses[questionId] = value;
        } else {
          // Check question type to determine format
          const question = getQuestionById(questionId);
          if (question) {
            if ((question.answerStyle === 'dropdown' || question.answerStyle === 'select') && question.allowMultipleAnswers && Array.isArray(value)) {
              // Multi-select dropdown/select
              updates.responses[questionId] = { answerIds: value };
            } else if ((question.answerStyle === 'dropdown' || question.answerStyle === 'select' || question.answerStyle === 'radio') && !question.allowMultipleAnswers && value) {
              // Single-select dropdown/select/radio
              updates.responses[questionId] = { answerId: value };
            } else {
              // Text fields and other types
              updates.responses[questionId] = { value };
            }
          } else {
            updates.responses[questionId] = { value };
          }
        }
      }

      saveChanges(updates);
    }
  }, [draft, form, saveChanges, getQuestionById, formState]);

  const markFieldTouched = useCallback((questionId: string) => {
    setFormState(prev => ({
      ...prev,
      touchedFields: new Set(prev.touchedFields).add(questionId),
    }));
  }, []);

  const setVisibleChildQuestions = useCallback((visibleSet: Set<string>) => {
    setFormState(prev => ({
      ...prev,
      visibleChildQuestions: visibleSet,
    }));
  }, []);

  const nextPage = useCallback(async () => {
    if (!pageData?.page) return;

    // Get all questions on current page
    const currentPageQuestions: Question[] = [];
    pageData.page.surveySections.forEach(section => {
      currentPageQuestions.push(...section.questions);
    });

    // Mark all fields as touched
    const newTouchedFields = new Set(formState.touchedFields);
    currentPageQuestions.forEach(q => {
      newTouchedFields.add(q.id);
      if (q.showComments) {
        newTouchedFields.add(`${q.id}_comment`);
      }
    });

    setFormState(prev => ({
      ...prev,
      touchedFields: newTouchedFields,
    }));

    // Validate current page
    const validation = validatePage(currentPageQuestions);

    if (!validation.isValid) {
      setFormState(prev => ({
        ...prev,
        validationErrors: validation.errors, // This replaces all errors with only current validation errors
      }));

      // Scroll to error summary at the top
      setTimeout(() => {
        const errorSummary = document.getElementById('validation-errors-summary');
        if (errorSummary && Object.keys(validation.errors).length > 0) {
          // Scroll to a position above the error summary to account for fixed header
          const yOffset = -100; // Adjust this value to scroll higher
          const y = errorSummary.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        } else if (Object.keys(validation.errors).length > 0) {
          // Fallback to scrolling to first error field
          const firstErrorId = Object.keys(validation.errors)[0];
          const errorElement = document.getElementById(`question-${firstErrorId}`);
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 100);

      return;
    }

    // Save any pending changes before navigating
    if (draft) {
      await saveNow();
    }

    // Clear errors and proceed to next page
    if (pageData?.survey?.totalPages && formState.currentPageIndex < pageData.survey.totalPages - 1) {
      window.scrollTo(0, 0);
      setFormState(prev => ({
        ...prev,
        currentPageIndex: prev.currentPageIndex + 1,
        validationErrors: {},
      }));
    }
  }, [pageData, formState.currentPageIndex, formState.touchedFields, validatePage, draft, saveNow]);

  const previousPage = useCallback(() => {
    if (formState.currentPageIndex > 0) {
      window.scrollTo(0, 0);
      setFormState(prev => ({
        ...prev,
        currentPageIndex: prev.currentPageIndex - 1,
      }));
    }
  }, [formState.currentPageIndex]);

  const submitForm = useCallback(async () => {
    if (!form || !draft) return;

    // Validate all pages before submission
    let hasErrors = false;
    const allErrors: Record<string, string> = {};

    // Validate current page
    if (pageData?.page) {
      const currentPageQuestions: Question[] = [];
      pageData.page.surveySections.forEach(section => {
        currentPageQuestions.push(...section.questions);
      });

      const validation = validatePage(currentPageQuestions);
      if (!validation.isValid) {
        hasErrors = true;
        Object.assign(allErrors, validation.errors);
      }
    }

    if (hasErrors) {
      setFormState(prev => ({
        ...prev,
        validationErrors: allErrors,
      }));

      // Scroll to error summary at the top
      setTimeout(() => {
        const errorSummary = document.getElementById('validation-errors-summary');
        if (errorSummary) {
          // Scroll to a position above the error summary to account for fixed header
          const yOffset = -100; // Adjust this value to scroll higher
          const y = errorSummary.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);

      return;
    }

    setFormState(prev => ({ ...prev, isSubmitting: true }));

    // Save any pending changes
    await saveNow();

    try {
      // Build the submission data
      const submissionData: FormResponses = {
        submission: {
          firstName: formState.formData.firstName as string,
          lastName: formState.formData.lastName as string,
          email: formState.formData.email as string,
          company: formState.formData.company as string,
          phone: formState.formData.phone as string,
        },
        responses: {}
      };

      // Add address if present
      if (formState.formData.address1 || formState.formData.name) {
        submissionData.submission.addressAttributes = {
          name: formState.formData.name as string || '',
          address_1: formState.formData.address1 as string || '',
          address_2: formState.formData.address2 as string || '',
          city: formState.formData.city as string || '',
          state: formState.formData.state as string || '',
          zip: formState.formData.zip as string || '',
          country: formState.formData.country as string || 'US',
        };
      }

      // Process question responses
      Object.entries(formState.formData).forEach(([key, value]) => {
        // Skip user fields and address fields
        if (['firstName', 'lastName', 'email', 'company', 'phone',
            'name', 'address1', 'address2', 'city', 'state', 'zip', 'country'].includes(key)) {
          return;
        }

        // Handle comment fields
        if (key.endsWith('_comment')) {
          const questionId = key.replace('_comment', '');
          if (!submissionData.responses[questionId]) {
            submissionData.responses[questionId] = {};
          }
          submissionData.responses[questionId].comment = value as string;
          return;
        }

        // Handle different answer types
        if (Array.isArray(value)) {
          // Multiple choice (checkboxes/multiselect/multi-dropdown)
          submissionData.responses[key] = { answerIds: value };
        } else if (typeof value === 'object' && value !== null && 'answerId' in value) {
          // Single choice (radio/dropdown) - legacy format
          submissionData.responses[key] = { answerId: value.answerId };
        } else if (typeof value === 'object' && value !== null && 'fileKey' in value) {
          // File upload
          submissionData.responses[key] = { fileKey: value.fileKey };
        } else if (typeof value === 'object' && value !== null && ('tagIds' in value || 'tagRequests' in value || 'questionResponseTaggings' in value)) {
          // Tag question - handle special data structure
          const tagValue = value as any;
          if (tagValue.tagIds && tagValue.tagIds.length > 0) {
            submissionData.responses[key] = { tagIds: tagValue.tagIds };
          }
          if (tagValue.tagRequests && tagValue.tagRequests.length > 0) {
            submissionData.responses[key] = {
              ...submissionData.responses[key],
              tagRequests: tagValue.tagRequests
            };
          }
          if (tagValue.questionResponseTaggings && tagValue.questionResponseTaggings.length > 0) {
            submissionData.responses[key] = {
              ...submissionData.responses[key],
              questionResponseTaggingsAttributes: tagValue.questionResponseTaggings
            };
          }
        } else {
          // Check question type to determine format
          const question = getQuestionById(key);
          if (question) {
            if ((question.answerStyle === 'dropdown' || question.answerStyle === 'select') && question.allowMultipleAnswers && Array.isArray(value)) {
              // Multi-select dropdown/select
              submissionData.responses[key] = { answerIds: value };
            } else if ((question.answerStyle === 'dropdown' || question.answerStyle === 'select' || question.answerStyle === 'radio') && !question.allowMultipleAnswers && typeof value === 'string' && value) {
              // Single-select dropdown/select/radio
              submissionData.responses[key] = { answerId: value };
            } else if (typeof value === 'string' || typeof value === 'number') {
              // Text fields and other types
              submissionData.responses[key] = { value };
            } else {
              // Default for other types
              submissionData.responses[key] = { value };
            }
          } else {
            // Default to value format
            submissionData.responses[key] = { value };
          }
        }
      });

      // Add auth token to submission data
      submissionData.authToken = draft.authToken;

      const result = await submitMutation.mutateAsync({
        formId: form.slug,
        responses: submissionData,
      });

      // Clear draft after successful submission
      clearDraft();

      setFormState(prev => ({
        ...prev,
        isSubmitting: false,
        showConfirmation: true,
        confirmationData: result,
      }));

      // Handle redirect if specified
      if (result.redirectUrl) {
        setTimeout(() => {
          window.location.href = result.redirectUrl;
        }, 2000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setFormState(prev => ({
        ...prev,
        isSubmitting: false,
        validationErrors: error instanceof Error && 'errors' in error
          ? (error as any).errors
          : { base: 'An error occurred submitting the form. Please try again.' }
      }));

      // Scroll to error summary at the top
      setTimeout(() => {
        const errorSummary = document.getElementById('validation-errors-summary');
        if (errorSummary) {
          // Scroll to a position above the error summary to account for fixed header
          const yOffset = -100; // Adjust this value to scroll higher
          const y = errorSummary.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);
    }
  }, [form, formState.formData, submitMutation, getQuestionById, draft, saveNow, clearDraft]);

  const handleBackToHome = () => {
    // If form has been started and has data, show confirmation
    if (hasStartedForm && Object.keys(formState.formData).length > 0 && !formState.showConfirmation) {
      setShowExitConfirmation(true);
    } else {
      navigate('/');
    }
  };

  const confirmExit = () => {
    // Clear draft and navigate away
    if (form?.slug || formSlug) {
      const draftKey = `form_draft_${form?.slug || formSlug}`;
      localStorage.removeItem(draftKey);
      queryClient.removeQueries({ queryKey: ['formDraft', form?.slug || formSlug] });
    }
    navigate('/');
  };

  const handleAuthenticated = () => {
    setIsAuthenticated(true);
    // If we haven't started the form yet, start it now
    if (!hasStartedForm) {
      setHasStartedForm(true);
      setShowWelcomePage(false);
    }
  };

  // Loading state
  if (isFormLoading || (form && hasStartedForm && isDraftLoading && !draft)) {
    return (
      <PageStyleProvider>
        <div className="min-h-screen flex flex-col relative">
          <PublicTopNavigation currentUser={currentUser || undefined} />
          <div className="flex-1 flex items-center justify-center pt-16 p-4">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="text-center">
                <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
                <p className="text-gray-600">
                  {isFormLoading ? 'Loading form...' : hasStartedForm && !draft ? 'Creating draft submission...' : 'Preparing your session...'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </PageStyleProvider>
    );
  }

  // Error state
  if (formError || !form) {
    return (
      <PageStyleProvider>
        <div className="min-h-screen flex flex-col relative">
          <PublicTopNavigation currentUser={currentUser || undefined} />
          <div className="flex-1 flex items-center justify-center p-4 pt-20">
            <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Form Not Found</h2>
              <p className="text-gray-600 mb-4">
                The form you're looking for doesn't exist or is no longer available.
              </p>
              <button
                onClick={handleBackToHome}
                className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </button>
            </div>
          </div>
        </div>
      </PageStyleProvider>
    );
  }

  // Check form availability
  if (!form.availability.isAvailable) {
    return (
      <PageStyleProvider>
        <div className="min-h-screen flex flex-col relative">
          <PublicTopNavigation currentUser={currentUser || undefined} />
          <div className="flex-1 flex items-center justify-center p-4 pt-20">
            <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Form Not Available</h2>
              <p className="text-gray-600 mb-4">
                {form.status === 'upcoming' && 'This form is not yet open for submissions.'}
                {form.status === 'closed' && 'This form is no longer accepting submissions.'}
                {form.status === 'disabled' && 'This form has been disabled.'}
              </p>
              <button
                onClick={handleBackToHome}
                className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </button>
            </div>
          </div>
        </div>
      </PageStyleProvider>
    );
  }

  // Show confirmation screen
  if (formState.showConfirmation) {
    return (
      <PageStyleProvider>
        <div className="min-h-screen flex flex-col relative">
          <PublicTopNavigation currentUser={currentUser || undefined} />
          <div className="flex-1 overflow-auto pt-16">
            <FormConfirmation
              message={formState.confirmationMessage || 'Thank you for your submission!'}
              confirmationData={formState.confirmationData}
              primaryColor={primaryColor}
              onBackToHome={handleBackToHome}
              onStartOver={() => {
                // Clear the existing draft to force a new submission
                const draftKey = `form_draft_${form.slug || formSlug}`;
                localStorage.removeItem(draftKey);
                queryClient.removeQueries({ queryKey: ['formDraft', form.slug || formSlug] });

                // Reset form state
                setFormState({
                  currentPageIndex: 0,
                  formData: {},
                  touchedFields: new Set(),
                  visibleChildQuestions: new Set(),
                  validationErrors: {},
                  isSubmitting: false,
                  showConfirmation: false,
                  confirmationData: undefined,
                });
                setShowWelcomePage(true);
                setHasStartedForm(false);
              }}
              onEditSubmission={() => {
                if (formState.confirmationData?.editUrl) {
                  navigate(formState.confirmationData.editUrl);
                }
              }}
            />
          </div>
        </div>
      </PageStyleProvider>
    );
  }

  // Show welcome page if not skipped and not started
  if (showWelcomePage && !hasStartedForm) {
    return (
      <PageStyleProvider>
        <div className="min-h-screen flex flex-col relative">
          <PublicTopNavigation currentUser={currentUser || undefined} />
          <div className="flex-1 overflow-auto pt-16">
            <FormWelcome
              form={form}
              primaryColor={primaryColor}
              onStart={() => {
                // Check if authentication is required
                if (requiresAuth && !isAuthenticated) {
                  // Show authentication required page instead of starting the form
                  setShowWelcomePage(false);
                  return;
                }

                // Clear any existing draft from localStorage to force a new submission
                const draftKey = `form_draft_${form.slug || formSlug}`;
                localStorage.removeItem(draftKey);

                // Mark that we're starting fresh
                setIsStartingFresh(true);

                // Reset built-in fields completed state for new submission
                setBuiltinFieldsCompleted(false);

                // Set hasStartedForm to true which will trigger draft creation
                setHasStartedForm(true);
                setShowWelcomePage(false);
              }}
            />
          </div>
        </div>
      </PageStyleProvider>
    );
  }

  // Show authentication required if needed
  if (requiresAuth && !isAuthenticated && !showWelcomePage) {
    return (
      <PageStyleProvider>
        <div className="min-h-screen flex flex-col relative">
          <PublicTopNavigation currentUser={currentUser || undefined} />
          <div className="flex-1 flex items-center justify-center p-4 pt-20 overflow-auto">
            <div className="max-w-2xl w-full">
              <AuthenticationRequired
                context="form"
                primaryColor={primaryColor}
                onAuthenticated={handleAuthenticated}
                entityName={form.name}
              />
            </div>
          </div>
        </div>
      </PageStyleProvider>
    );
  }

  // Show built-in fields step if needed
  console.log('Built-in fields check:', {
    hasStartedForm,
    showWelcomePage,
    showConfirmation: formState.showConfirmation,
    shouldShowBuiltinFields: shouldShowBuiltinFields(),
    builtinFieldsCompleted
  });

  if (hasStartedForm && !showWelcomePage && !formState.showConfirmation && shouldShowBuiltinFields() && !builtinFieldsCompleted) {
    return (
      <PageStyleProvider>
        <div className="min-h-screen flex flex-col relative">
          <PublicTopNavigation currentUser={currentUser || undefined} />
          <div className="flex-1 flex items-start justify-center overflow-auto pt-16">
            <div className="container mx-auto px-4 py-20">
              <div className="max-w-2xl mx-auto">
                <FormBuiltinFields
                  form={form}
                  formData={formState.formData}
                  onUpdateField={updateField}
                  onContinue={() => {
                    setBuiltinFieldsCompleted(true);
                  }}
                  onBack={() => {
                    setShowWelcomePage(true);
                    setHasStartedForm(false);
                  }}
                  primaryColor={primaryColor}
                  isSaving={isSaving}
                />
              </div>
            </div>
          </div>
        </div>
      </PageStyleProvider>
    );
  }

  const formActions = {
    updateField,
    markFieldTouched,
    setVisibleChildQuestions,
    nextPage,
    previousPage,
    submitForm,
  };

  return (
    <FormContext.Provider value={{ state: formState, actions: formActions }}>
      <PageStyleProvider>
        <div className="min-h-screen flex flex-col relative">
          {/* Public Top Navigation */}
          <PublicTopNavigation currentUser={currentUser || undefined} />

          <div className="flex-1 overflow-auto pt-16 overscroll-y-none">
            <div className="container mx-auto px-4 py-8 min-h-full">
              <div className="max-w-3xl mx-auto min-h-full mb-32">
              {/* Header */}
              <div className="mb-8">
                <button
                  onClick={handleBackToHome}
                  className="flex items-center text-gray-600 hover:text-gray-900 bg-white px-3 py-1.5 rounded-lg shadow-sm mb-4"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Exit
                </button>

                {/* Form header */}
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex justify-between items-start mb-2">
                    <h1 className="text-3xl font-bold text-gray-900">{form.name}</h1>
                    {draft && (
                      <AutosaveIndicator
                        isSaving={isSaving}
                        lastSaved={lastSaved}
                        error={saveError}
                      />
                    )}
                  </div>
                  {form.description && (
                    <div
                      className="prose prose-sm max-w-none text-gray-700"
                      dangerouslySetInnerHTML={{ __html: form.description }}
                    />
                  )}

                  {/* Progress Bar */}
                  {pageData?.survey?.totalPages && pageData.survey.totalPages > 1 && (
                    <FormProgressBar
                      current={formState.currentPageIndex}
                      total={pageData.survey.totalPages}
                      primaryColor={primaryColor}
                    />
                  )}
                </div>
              </div>

              {/* Validation Errors Summary */}
              {Object.keys(formState.validationErrors).length > 0 && (
                <div id="validation-errors-summary" className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-2" />
                    <div>
                      <h3 className="text-sm font-medium text-red-800">Please correct the following errors:</h3>
                      <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                        {Object.entries(formState.validationErrors).map(([fieldId, error]) => (
                          <li key={fieldId}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Form Content */}
              {isPageLoading ? (
                <div className="bg-[#fffffff7] backdrop-blur-sm rounded-lg shadow-lg p-8 text-center">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
                  <p className="text-gray-600">Loading page...</p>
                </div>
              ) : pageData?.page ? (
                <>
                  <FormPage
                    page={pageData.page}
                    primaryColor={primaryColor}
                  />
                  <FormNavigation
                    currentPage={formState.currentPageIndex}
                    totalPages={pageData.survey?.totalPages || 1}
                    isLastPage={formState.currentPageIndex === (pageData.survey?.totalPages || 1) - 1}
                    isSubmitting={formState.isSubmitting}
                    primaryColor={primaryColor}
                  />
                  {/* Extra space after form content to allow overscrolling */}
                  <div className="h-72" aria-hidden="true" />
                </>
              ) : (
                <div className="bg-[#fffffff7] backdrop-blur-sm rounded-lg shadow-lg p-8 text-center">
                  <p className="text-gray-600">No questions available for this form.</p>
                </div>
              )}
              </div>
            </div>
          </div>
        </div>

        {/* Exit Confirmation Dialog */}
        {showExitConfirmation && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                confirmExit();
              } else if (e.key === 'Escape') {
                setShowExitConfirmation(false);
              }
            }}
          >
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Are you sure you want to leave?
              </h3>
              <p className="text-gray-600 mb-6">
                All progress will be lost and you'll need to start over.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowExitConfirmation(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmExit}
                  className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                >
                  Leave Form
                </button>
              </div>
            </div>
          </div>
        )}
      </PageStyleProvider>
    </FormContext.Provider>
  );
};

// Main component that wraps with StyleProvider for form-specific styles
export const PublicForm: React.FC = () => {
  const { teamStyle } = useTeamContext();
  const { formSlug } = useParams<{ formSlug: string }>();
  const { data: form, isLoading, error } = usePublicForm(formSlug || '');

  // If loading or error, render without style provider
  if (isLoading || error || !form) {
    return <PublicFormContent />;
  }

  // Wrap with StyleProvider to override team styles with form styles
  return (
    <StyleProvider
      teamStyle={teamStyle}
      pageStyle={form.style || form.options?.style}
    >
      <PublicFormContent />
    </StyleProvider>
  );
};